import { useEffect, useMemo, useState } from "react";
import { api, downloadXlsx } from "@/lib/api";
import { Loader2, CheckCheck, ChevronLeft, ChevronRight, Download, FileSpreadsheet } from "lucide-react";

const STATUS_COLORS = {
  present: { bg: "#D8F3DC", color: "#1B4332", short: "P" },
  late: { bg: "#FCE7C9", color: "#9A6020", short: "L" },
  absent: { bg: "#FADED7", color: "#9A3412", short: "A" },
  leave: { bg: "#DCE9F0", color: "#1F4863", short: "LV" },
  half_day: { bg: "#F4F1DE", color: "#57534E", short: "HD" },
};
const STATUSES = [
  ["present", "Present"], ["late", "Late"], ["half_day", "Half Day"],
  ["absent", "Absent"], ["leave", "Leave"],
];
// Click cycle order — empty cell goes to "present"; subsequent clicks cycle;
// the 6th click ("clear") deletes the mark and returns the cell to blank/WO.
const CYCLE = ["present", "late", "half_day", "absent", "leave", "clear"];

export default function Attendance() {
  const [tab, setTab] = useState("register"); // register | monthly | logs

  return (
    <div className="space-y-6" data-testid="attendance-page">
      <header>
        <div className="kpi-label" style={{ color: "var(--color-secondary)" }}>Daily Logs</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">Attendance</h1>
        <p className="text-stone-600 mt-1 text-sm">Mark daily attendance, edit the whole month at a glance, or review GPS punch logs.</p>
      </header>

      <div className="flex items-center gap-1 border-b border-stone-200">
        {[["register", "Daily Register (Mark)"], ["monthly", "Monthly View & Edit"], ["logs", "Logs & History"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`attendance-tab-${k}`}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-current" : "border-transparent text-stone-500 hover:text-stone-800"}`}
                  style={tab === k ? { color: "var(--color-primary)" } : {}}>
            {l}
          </button>
        ))}
      </div>

      {tab === "register" && <DailyRegister />}
      {tab === "monthly" && <MonthlyView />}
      {tab === "logs" && <Logs />}
    </div>
  );
}

/* ========== Shared utilities ========== */
function daysInMonth(month /* "YYYY-MM" */) {
  const [y, m] = month.split("-").map((x) => parseInt(x, 10));
  return new Date(y, m, 0).getDate();
}
function dayDate(month, day) {
  return `${month}-${String(day).padStart(2, "0")}`;
}
function dayLabel(month, day) {
  const [y, m] = month.split("-").map((x) => parseInt(x, 10));
  return new Date(y, m - 1, day).toLocaleDateString("en-IN", { weekday: "short" });
}

/* ==================== DAILY REGISTER (mark attendance) ==================== */
function DailyRegister() {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState({}); // employee_id -> attendance rec
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [q, setQ] = useState("");
  const [dept, setDept] = useState("");

  const load = async () => {
    setLoading(true);
    const [e, a] = await Promise.all([
      api.get("/employees"),
      api.get("/attendance", { params: { date_from: date, date_to: date } }),
    ]);
    // Show employees who were on the roster on `date`: joined on/before and
    // NOT exited before that date. This includes exited employees on their
    // historical dates and hides new hires from pre-joining dates.
    setEmployees(e.data.filter((x) =>
      (!x.joining_date || x.joining_date <= date) &&
      (!x.exit_date || x.exit_date >= date)
    ));
    setRecords(Object.fromEntries(a.data.map((r) => [r.employee_id, r])));
    setLoading(false);
  };
  useEffect(() => { load(); }, [date]);

  const mark = async (empId, status) => {
    setSavingId(empId);
    const { data } = await api.post("/attendance", { employee_id: empId, date, status });
    setRecords((r) => ({ ...r, [empId]: data }));
    setSavingId(null);
  };

  const departments = useMemo(() => {
    const s = new Set();
    employees.forEach((e) => { const d = (e.department_name || "").trim(); if (d) s.add(d); });
    return Array.from(s).sort();
  }, [employees]);

  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    return employees.filter((e) => {
      if (dept && (e.department_name || "") !== dept) return false;
      if (!term) return true;
      return e.full_name?.toLowerCase().includes(term)
        || e.department_name?.toLowerCase().includes(term)
        || e.designation?.toLowerCase().includes(term);
    });
  }, [employees, q, dept]);

  const markAllPresent = async () => {
    setBulkBusy(true);
    const unmarked = filtered.filter((e) => !records[e.id]);
    if (unmarked.length) {
      await api.post("/attendance/bulk", {
        items: unmarked.map((e) => ({ employee_id: e.id, date, status: "present" })),
      });
      // Reload to refresh records
      const { data: a } = await api.get("/attendance", { params: { date_from: date, date_to: date } });
      setRecords(Object.fromEntries(a.map((r) => [r.employee_id, r])));
    }
    setBulkBusy(false);
  };

  const markedCount = filtered.filter((e) => records[e.id]).length;

  return (
    <div className="space-y-4" data-testid="daily-register">
      <div className="flex items-center gap-3 flex-wrap">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="register-date"
               className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white" />
        <select value={dept} onChange={(e) => setDept(e.target.value)} data-testid="register-dept-filter"
                className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white">
          <option value="">All Departments</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <input placeholder="Search employee…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="register-search"
               className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white w-52" />
        <div className="text-sm text-stone-500">{markedCount} / {filtered.length} marked</div>
        <button onClick={markAllPresent} disabled={bulkBusy || markedCount === filtered.length} data-testid="mark-all-present"
                className="ml-auto flex items-center gap-2 px-4 py-2 rounded-md text-white font-medium disabled:opacity-50"
                style={{ background: "var(--color-primary)" }}>
          {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />}
          Mark Remaining Present
        </button>
      </div>

      {loading ? <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="card-flat p-0 overflow-hidden">
          <table className="w-full text-sm" data-testid="register-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-500 bg-stone-50/60">
              <th className="px-5 py-3">Employee</th><th>Department</th><th>In / Out</th><th>Mark Status</th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan="4" className="py-10 text-center text-stone-500">No active employees found.</td></tr>}
              {filtered.map((e) => {
                const rec = records[e.id];
                return (
                  <tr key={e.id} className="border-t border-stone-100" data-testid={`register-row-${e.id}`}>
                    <td className="px-5 py-2.5">
                      <div className="font-medium">{e.full_name}</div>
                      <div className="text-xs text-stone-500">{e.designation}</div>
                    </td>
                    <td className="text-xs text-stone-600">{e.department_name || "—"}</td>
                    <td className="text-xs text-stone-600 whitespace-nowrap">
                      {rec?.in_time || "—"} / {rec?.out_time || "—"}
                    </td>
                    <td className="py-2">
                      <div className="flex gap-1 flex-wrap items-center">
                        {STATUSES.map(([key, label]) => {
                          const active = rec?.status === key;
                          const c = STATUS_COLORS[key];
                          return (
                            <button key={key} onClick={() => mark(e.id, key)} disabled={savingId === e.id}
                                    data-testid={`mark-${key}-${e.id}`}
                                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${active ? "" : "border-stone-200 text-stone-500 hover:bg-stone-50"}`}
                                    style={active ? { background: c.bg, color: c.color, borderColor: c.color } : {}}>
                              {label}
                            </button>
                          );
                        })}
                        {savingId === e.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-400" />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ==================== MONTHLY VIEW (calendar grid + bulk edit) ==================== */
function MonthlyView() {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState({}); // `${empId}|${date}` -> rec
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [bulkRowId, setBulkRowId] = useState(null);
  const [dept, setDept] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const days = daysInMonth(month);
    const monthEnd = dayDate(month, days);
    const monthStart = `${month}-01`;
    const [e, a] = await Promise.all([
      api.get("/employees"),
      api.get("/attendance", {
        params: { date_from: monthStart, date_to: monthEnd },
      }),
    ]);
    // Show employees who were on the roster during ANY part of this month:
    // joined on/before month_end AND (still active OR exited on/after month_start).
    // This restores exited employees on their historical months and hides
    // new joiners from months before their joining_date.
    setEmployees(e.data.filter((x) =>
      (!x.joining_date || x.joining_date <= monthEnd) &&
      (!x.exit_date || x.exit_date >= monthStart)
    ));
    setRecords(Object.fromEntries(a.data.map((r) => [`${r.employee_id}|${r.date}`, r])));
    setLoading(false);
  };
  useEffect(() => { load(); }, [month]);

  const departments = useMemo(() => {
    const s = new Set();
    employees.forEach((e) => { const d = (e.department_name || "").trim(); if (d) s.add(d); });
    return Array.from(s).sort();
  }, [employees]);

  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    return employees.filter((e) => {
      if (dept && (e.department_name || "") !== dept) return false;
      if (!term) return true;
      return e.full_name?.toLowerCase().includes(term)
        || e.designation?.toLowerCase().includes(term);
    });
  }, [employees, q, dept]);

  const days = daysInMonth(month);

  // Click a cell → cycle through statuses:
  //   empty → P → L → HD → A → LV → clear (deleted, back to empty/WO) → P → ...
  const cycleCell = async (emp, day) => {
    const date = dayDate(month, day);
    const key = `${emp.id}|${date}`;
    const cur = records[key]?.status;
    const idx = CYCLE.indexOf(cur);
    const next = CYCLE[(idx + 1) % CYCLE.length]; // wraps; -1 (empty) → 0 (present)
    setSavingKey(key);
    if (next === "clear") {
      // Delete the record so the cell renders as empty (WO on Sundays)
      await api.delete("/attendance", { params: { employee_id: emp.id, date } });
      setRecords((r) => {
        const rest = { ...r };
        delete rest[key];
        return rest;
      });
    } else {
      const { data } = await api.post("/attendance", { employee_id: emp.id, date, status: next });
      setRecords((r) => ({ ...r, [key]: data }));
    }
    setSavingKey(null);
  };

  const markRowPresent = async (emp) => {
    setBulkRowId(emp.id);
    const items = [];
    for (let d = 1; d <= days; d++) {
      const date = dayDate(month, d);
      // Skip Sundays AND days that already have any status
      const [y, m] = month.split("-").map(parseFloat);
      const dow = new Date(y, m - 1, d).getDay(); // 0=Sun
      if (dow === 0) continue;
      if (records[`${emp.id}|${date}`]) continue;
      items.push({ employee_id: emp.id, date, status: "present" });
    }
    if (items.length) {
      await api.post("/attendance/bulk", { items });
      const a = await api.get("/attendance", {
        params: { date_from: `${month}-01`, date_to: dayDate(month, days) },
      });
      setRecords(Object.fromEntries(a.data.map((r) => [`${r.employee_id}|${r.date}`, r])));
    }
    setBulkRowId(null);
  };

  const counts = (emp) => {
    const c = { present: 0, late: 0, half_day: 0, absent: 0, leave: 0 };
    for (let d = 1; d <= days; d++) {
      const rec = records[`${emp.id}|${dayDate(month, d)}`];
      if (rec && c[rec.status] !== undefined) c[rec.status]++;
    }
    return c;
  };

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map((x) => parseInt(x, 10));
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const exportCsv = () => {
    const monthName = new Date(`${month}-01`).toLocaleString("en-IN", { month: "long", year: "numeric" }).toUpperCase();
    const dayCols = Array.from({ length: days }, (_, i) => i + 1);
    const csvEscape = (s) => {
      const str = String(s ?? "");
      return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const lines = [];
    lines.push(csvEscape(`ATTENDANCE REGISTER — ${monthName}`));
    if (dept) lines.push(csvEscape(`Department: ${dept}`));
    lines.push("");
    // Day header rows (day-of-week + day-number)
    const header1 = ["", "", "", ...dayCols.map((d) => dayLabel(month, d).slice(0, 1)), "P", "A", "LV", "Working Days"];
    const header2 = ["Employee Name", "Department", "Designation", ...dayCols.map((d) => String(d)), "", "", "", ""];
    lines.push(header1.map(csvEscape).join(","));
    lines.push(header2.map(csvEscape).join(","));
    filtered.forEach((emp) => {
      const c = counts(emp);
      const cells = dayCols.map((d) => {
        const date = dayDate(month, d);
        const rec = records[`${emp.id}|${date}`];
        if (rec) return STATUS_COLORS[rec.status]?.short || "";
        const [y, m] = month.split("-").map((x) => parseInt(x, 10));
        return new Date(y, m - 1, d).getDay() === 0 ? "WO" : "";
      });
      const workingDays = c.present + c.late + c.half_day * 0.5;
      lines.push([
        emp.full_name || "",
        emp.department_name || "",
        emp.designation || "",
        ...cells,
        c.present + c.late,
        c.absent,
        c.leave,
        workingDays,
      ].map(csvEscape).join(","));
    });
    // Append legend at bottom
    lines.push("");
    lines.push("Legend:,P = Present,L = Late,HD = Half Day,A = Absent,LV = Leave,WO = Weekly Off");
    const csv = lines.join("\n");
    // BOM so Excel opens UTF-8 correctly
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `attendance_${month}${dept ? "_" + dept.replace(/\s+/g, "_") : ""}.csv`;
    a.click();
  };

  return (
    <div className="space-y-4" data-testid="monthly-view">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <button onClick={() => shiftMonth(-1)} data-testid="prev-month"
                  className="p-2 rounded-md border border-stone-300 hover:bg-stone-50"><ChevronLeft className="w-4 h-4" /></button>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="monthly-month"
                 className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white font-medium min-w-[9rem]" />
          <button onClick={() => shiftMonth(1)} data-testid="next-month"
                  className="p-2 rounded-md border border-stone-300 hover:bg-stone-50"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <select value={dept} onChange={(e) => setDept(e.target.value)} data-testid="monthly-dept-filter"
                className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white min-w-[12rem]">
          <option value="">All Departments</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <input placeholder="Search employee…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="monthly-search"
               className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white w-52" />
        <button onClick={exportCsv} disabled={loading || filtered.length === 0} data-testid="export-monthly-csv"
                className="flex items-center gap-2 px-3 py-2 rounded-md border border-stone-300 text-sm font-medium hover:bg-stone-50 disabled:opacity-50">
          <Download className="w-4 h-4" /> Export CSV
        </button>
        <button
          onClick={() => downloadXlsx("/attendance/export.xlsx",
            `attendance_${month}${dept ? "_" + dept.replace(/\s+/g, "_") : ""}.xlsx`,
            { month, ...(dept ? { department: dept } : {}) })}
          disabled={loading || filtered.length === 0}
          data-testid="export-monthly-xlsx"
          className="flex items-center gap-2 px-3 py-2 rounded-md text-white text-sm font-medium disabled:opacity-50"
          style={{ background: "#1B4332" }}>
          <FileSpreadsheet className="w-4 h-4" /> Excel
        </button>
        <div className="ml-auto text-xs text-stone-500 leading-tight">
          <div>Click a cell to cycle: <b>P → L → HD → A → LV → clear (back to WO)</b></div>
          <div>Sundays are shown as <b>WO</b> (weekly off) — click to mark anyway.</div>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 flex-wrap text-xs">
        {STATUSES.map(([key, label]) => {
          const c = STATUS_COLORS[key];
          return (
            <span key={key} className="inline-flex items-center gap-1.5">
              <span className="inline-block w-4 h-4 rounded text-[10px] font-bold flex items-center justify-center"
                    style={{ background: c.bg, color: c.color }}>{c.short}</span>
              {label}
            </span>
          );
        })}
      </div>

      {loading ? <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="card-flat p-0 overflow-auto" style={{ maxHeight: "70vh" }}>
          <table className="text-xs border-collapse" data-testid="monthly-table" style={{ minWidth: "max-content" }}>
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-stone-200">
                <th className="px-3 py-2 text-left bg-stone-50 sticky left-0 z-20 border-r border-stone-200" style={{ minWidth: 200 }}>Employee</th>
                {Array.from({ length: days }, (_, i) => {
                  const d = i + 1;
                  const dow = dayLabel(month, d);
                  const isSun = dow === "Sun";
                  return (
                    <th key={d} className={`px-1 py-1 text-center font-semibold w-9 ${isSun ? "bg-red-50/40 text-red-700" : "bg-stone-50 text-stone-600"}`}>
                      <div className="text-[10px] opacity-70">{dow.slice(0, 1)}</div>
                      <div>{d}</div>
                    </th>
                  );
                })}
                <th className="px-2 py-2 text-center bg-stone-50 border-l-2 border-stone-300 text-[10px]" style={{ minWidth: 80 }}>P / A / LV</th>
                <th className="px-2 py-2 text-center bg-stone-50" style={{ minWidth: 110 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={days + 3} className="py-10 text-center text-stone-500">No employees match these filters.</td></tr>
              )}
              {filtered.map((emp) => {
                const c = counts(emp);
                return (
                  <tr key={emp.id} className="border-b border-stone-100 hover:bg-stone-50/40" data-testid={`monthly-row-${emp.id}`}>
                    <td className="px-3 py-1.5 sticky left-0 z-10 bg-white border-r border-stone-200">
                      <div className="font-medium text-sm">{emp.full_name}</div>
                      <div className="text-[10px] text-stone-500">{emp.department_name || emp.designation || "—"}</div>
                    </td>
                    {Array.from({ length: days }, (_, i) => {
                      const d = i + 1;
                      const date = dayDate(month, d);
                      const key = `${emp.id}|${date}`;
                      const rec = records[key];
                      const [y, m] = month.split("-").map((x) => parseInt(x, 10));
                      const isSun = new Date(y, m - 1, d).getDay() === 0;
                      const isSaving = savingKey === key;
                      const st = rec?.status;
                      const col = st ? STATUS_COLORS[st] : null;
                      return (
                        <td key={d} className="p-0.5 text-center">
                          <button onClick={() => cycleCell(emp, d)} disabled={isSaving}
                                  data-testid={`cell-${emp.id}-${d}`}
                                  className="w-8 h-7 rounded text-[10px] font-bold transition border"
                                  style={col
                                    ? { background: col.bg, color: col.color, borderColor: col.color }
                                    : { background: isSun ? "#FEF2F2" : "white", color: isSun ? "#9F1239" : "#A8A29E", borderColor: "#E7E5E4" }}>
                            {isSaving ? "…" : (col ? col.short : (isSun ? "WO" : "·"))}
                          </button>
                        </td>
                      );
                    })}
                    <td className="px-2 text-center border-l-2 border-stone-300 whitespace-nowrap">
                      <span style={{ color: STATUS_COLORS.present.color }} className="font-bold">{c.present + c.late + c.half_day * 0.5}</span>
                      <span className="text-stone-400"> / </span>
                      <span style={{ color: STATUS_COLORS.absent.color }} className="font-bold">{c.absent}</span>
                      <span className="text-stone-400"> / </span>
                      <span style={{ color: STATUS_COLORS.leave.color }} className="font-bold">{c.leave}</span>
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button onClick={() => markRowPresent(emp)} disabled={bulkRowId === emp.id}
                              data-testid={`mark-row-present-${emp.id}`}
                              className="px-2 py-1 rounded text-[10px] font-semibold text-white whitespace-nowrap"
                              style={{ background: "var(--color-primary)" }}>
                        {bulkRowId === emp.id ? "…" : "Fill P"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ==================== LOGS & HISTORY (existing view) ==================== */
function Logs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/attendance", { params: { date_from: dateFrom, date_to: dateTo } });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [dateFrom, dateTo]);

  const summary = items.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <div className="kpi-label mb-1">From</div>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from"
                 className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white" />
        </div>
        <div>
          <div className="kpi-label mb-1">To</div>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to"
                 className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["present", "late", "absent", "leave", "half_day"].map((s) => (
          <div key={s} className="card-flat" style={{ padding: 16 }}>
            <div className="kpi-label capitalize">{s.replace("_", " ")}</div>
            <div className="font-heading text-2xl font-bold mt-1" style={{ color: STATUS_COLORS[s].color }}>{summary[s] || 0}</div>
          </div>
        ))}
      </div>

      <div className="card-flat overflow-hidden p-0">
        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : (
          <table className="w-full text-sm" data-testid="attendance-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-stone-500 bg-stone-50/60">
                <th className="px-6 py-3">Date</th>
                <th className="px-6 py-3">Employee</th>
                <th className="px-6 py-3">In</th>
                <th className="px-6 py-3">Out</th>
                <th className="px-6 py-3">Status</th>
                <th className="px-6 py-3">Marked By</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan="6" className="py-10 text-center text-stone-500">No records</td></tr>}
              {items.slice(0, 200).map((a) => {
                const c = STATUS_COLORS[a.status] || STATUS_COLORS.present;
                return (
                  <tr key={a.id} className="border-t border-stone-100 hover:bg-stone-50/50 transition">
                    <td className="px-6 py-3">{a.date}</td>
                    <td className="px-6 py-3 font-medium">{a.employee_name}</td>
                    <td className="px-6 py-3 text-stone-600">{a.in_time || "—"}</td>
                    <td className="px-6 py-3 text-stone-600">{a.out_time || "—"}</td>
                    <td className="px-6 py-3">
                      <span className="status-pill capitalize" style={{ background: c.bg, color: c.color }}>{a.status.replace("_", " ")}</span>
                    </td>
                    <td className="px-6 py-3 text-xs text-stone-500">{a.marked_by || "GPS Punch"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
